import React, { useContext, useState, useEffect, useReducer, useRef, memo, useCallback } from "react";
import { isSameDay, parseISO, format } from "date-fns";
import clsx from "clsx";
import { isNil } from "lodash";
import { blue, green, red } from "@material-ui/core/colors";
import path from "path";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
  Badge
} from "@material-ui/core";

import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  Facebook,
  Instagram,
  Reply,
  Close,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";
import YouTubePreview from "../ModalYoutubeCors";

import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";

import api from "../../services/api";
import toastError from "../../errors/toastError";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";
import SelectMessageCheckbox from "./SelectMessageCheckbox";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";
import AudioModal from "../AudioModal";
import { messages } from "../../translate/languages";
import { useParams, useHistory, useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
  },

  currentTick: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "95%",
    backgroundColor: theme.palette.primary.main,
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "1px 5px 10px #b3b3b3",
  },

  currentTicktText: {
    color: theme.palette.primary,
    fontWeight: 'bold',
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  messagesList: {
    backgroundImage: theme.mode === 'light' ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`,
    backgroundColor: theme.mode === 'light' ? "transparent" : "#0b0b0d",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 30px 20px", // Manter o padding original
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down('sm')]: { // Aplicar apenas em telas menores que 'small' (mobile)
        paddingBottom: "80px", // Aumentado o padding-bottom para 80px em dispositivos móveis
    },
},
  dragElement: {
    background: 'rgba(255, 255, 255, 0.8)',
    position: "absolute",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },


  circleLoading: {
    color: blue[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "flex",
    alignItems: "center",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.mode === 'light' ? "#ffffff" : "#202c33",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.mode === 'light' ? "#f0f0f0" : "#1d282f",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#388aff",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "flex",
    alignItems: "center",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: theme.mode === 'light' ? "#dcf8c6" : "#005c4b",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  messageRightPrivate: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "flex",
    alignItems: "center",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: "#F0E68C",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  buttonsMessageHeader: {
    fontWeight: 600,
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  buttonsMessageContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginTop: 6,
    paddingBottom: 6,
    width: '100%'
  },
  buttonsMessageButton: {
    textTransform: 'none',
    justifyContent: 'center',
    borderRadius: 8,
    border: 'none',
    boxShadow: 'none',
    width: '100%',
    padding: '8px 12px',
    minHeight: 40,
    fontWeight: 600,
    fontSize: '14px',
    backgroundColor: theme.mode === 'light' ? '#f0f0f0' : '#2a2f32',
    color: '#00725a'
  },
  buttonsMessageButtonText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    textAlign: 'center'
  },
  buttonsMessageButtonDisabled: {
    pointerEvents: 'none',
    cursor: 'default',
    opacity: 1
  },
  buttonsMessageTime: {
    fontSize: '11px',
    position: 'absolute',
    top: '5px',
    right: '10px',
    color: '#999',
    fontWeight: 400
  },

  messageLeftButtons: {
    backgroundColor: theme.mode === 'light' ? "#ffffff" : "#202c33",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  messageRightButtons: {
    backgroundColor: theme.mode === 'light' ? "#dcf8c6" : "#005c4b",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.mode === 'light' ? "#cfe9ba" : "#025144",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: "100%",
    maxWidth: 400,
    height: "auto",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    [theme.breakpoints.down('sm')]: {
      maxWidth: "100%",
      width: "100%",
    },
  },

  timestamp: {
    fontSize: "11px", // Valor numérico com unidade entre aspas
    position: "absolute",
    bottom: "5px", // Valor com unidade em string
    right: "10px", // Valor com unidade em string
    color: "#999",
  },
  
  forwardMessage: {
    fontSize: "12px", // Valor numérico com unidade entre aspas
    fontStyle: "italic",
    position: "absolute",
    top: "5px", // Valor com unidade em string
    left: "10px", // Valor com unidade em string
    color: "#999",
    display: "flex",
    alignItems: "center",
  },
  
  dailyTimestamp: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px", // Valor com unidade em string
    backgroundColor: "#e1f3fb",
    margin: "10px", // Valor com unidade em string
    borderRadius: "10px", // Valor com unidade em string
    boxShadow: "0 1px 1px #b3b3b3",
  },
  

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: blue[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackPlayedIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },
  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
    color: theme.mode === "light" ? theme.palette.light : theme.palette.dark,
  },

  messageCenter: {
    marginTop: 5,
    alignItems: "center",
    verticalAlign: "center",
    alignContent: "center",
    backgroundColor: "#E1F5FEEB",
    fontSize: "12px",
    minWidth: 100,
    maxWidth: 270,
    color: "#272727",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  deletedMessage: {
    color: '#f55d65'
  },
  
  messageCheckboxLeft: {
    padding: '4px',
    marginRight: '8px',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    minHeight: '40px',
    '& .MuiCheckbox-root': {
      padding: '2px',
      '& .MuiSvgIcon-root': {
        fontSize: '18px',
      }
    }
  },

  messageCheckboxRight: {
    padding: '4px',
    marginLeft: '8px',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    minHeight: '40px',
    '& .MuiCheckbox-root': {
      padding: '2px',
      '& .MuiSvgIcon-root': {
        fontSize: '18px',
      }
    }
  },
  
  messageContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },

  messageContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: 'auto',
    position: 'relative',
    width: '100%'
  },

  messageContainerLeft: {
    justifyContent: 'flex-start',
    alignSelf: 'flex-start'
  },

  messageContainerRight: {
    justifyContent: 'flex-end',
    alignSelf: 'flex-end'
  },

  audioContainer: {
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '280px', // Largura máxima para mobile
      margin: '0 auto', // Centralizar
    },
  },

  messageLongPressing: {
    opacity: 0.7,
    transform: 'scale(0.98)',
    transition: 'all 0.1s ease-in-out'
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "LOAD_MESSAGES_FIRST_PAGE") {
    // For the first page, replace the entire state
    return action.payload;
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  // Fallback para ações não reconhecidas
  console.warn("Unknown action type:", action.type);
  return state;
};

const MessagesList = ({
  isGroup,
  onDrop,
  whatsappId,
  queueId,
  channel
}) => {
  const classes = useStyles();
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const { ticketId } = useParams();

  // Estados para long press
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [pressedMessageId, setPressedMessageId] = useState(null);

  const currentTicketId = useRef(ticketId);
  const { getAll } = useCompanySettings();
  const [dragActive, setDragActive] = useState(false);

  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState(false);
  const { selectedQueuesMessage } = useContext(QueueSelectedContext);

  const { isSelectionMode, setIsSelectionMode } = useContext(ForwardMessageContext);

  const { user, socket } = useContext(AuthContext);

  const companyId = user.companyId;

  // Detectar parâmetro de busca da URL
  const searchParams = new URLSearchParams(location.search);
  const searchTerm = searchParams.get('search');

  // Funções para long press
  const handleLongPressStart = (message) => {
    if (pressedMessageId) return;
    
    setPressedMessageId(message.id);
    const timer = setTimeout(() => {
      if (!isSelectionMode) {
        setIsSelectionMode(true);
      }
      setPressedMessageId(null);
    }, 500); // 500ms para ativar o long press
    
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setPressedMessageId(null);
  };

  const handleLongPressCancel = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setPressedMessageId(null);
  };
  




  // Função para destacar texto em uma mensagem
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text || searchTerm.trim() === '' || typeof searchTerm !== 'string' || typeof text !== 'string') {
      return text;
    }
    
    try {
      // Garantir que text é uma string válida
      const textStr = String(text);
      
      // Verificar se o texto contém o termo de busca (case insensitive)
      if (!textStr.toLowerCase().includes(searchTerm.toLowerCase())) {
        return textStr;
      }
      
      // Escapar caracteres especiais do HTML para evitar problemas de segurança
      const escapedText = textStr
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;');
      
      // Escapar caracteres especiais do regex
      const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
      
      const result = escapedText.replace(regex, '<strong style="background-color: #ffeb3b; padding: 1px 2px; border-radius: 2px; color: #000;">$1</strong>');
      
      return result;
    } catch (error) {
      console.error("Error highlighting search term:", error);
      return String(text || '');
    }
  };

  // Função alternativa mais segura para destacar texto
  const highlightSearchTermSafe = (text, searchTerm) => {
    if (!searchTerm || !text || searchTerm.trim() === '' || typeof searchTerm !== 'string' || typeof text !== 'string') {
      return text;
    }
    
    try {
      const textStr = String(text);
      const searchLower = searchTerm.toLowerCase();
      const textLower = textStr.toLowerCase();
      
      if (!textLower.includes(searchLower)) {
        return textStr;
      }
      
      // Encontrar a posição do termo de busca
      const index = textLower.indexOf(searchLower);
      const before = textStr.substring(0, index);
      const match = textStr.substring(index, index + searchTerm.length);
      const after = textStr.substring(index + searchTerm.length);
      
      return `${before}**${match}**${after}`;
    } catch (error) {
      console.error("Error in safe highlighting:", error);
      return String(text || '');
    }
  };

  const truncateButtonText = (text) => {
    const s = String(text || '').trim();
    if (s.length <= 30) return s;
    return s.slice(0, 30) + '...';
  };

  





  // Removed conflicting useEffect - consolidated into the main searchTerm useEffect below

  // Mostrar IMEDIATAMENTE a última mensagem quando as mensagens carregarem
  useEffect(() => {
    if (!loading && !searchTerm && messagesList.length > 0) {
      // Múltiplas tentativas para garantir que funcione em TODAS as conversas (incluindo mídias)
      const scrollToLastMessage = () => {
        if (lastMessageRef.current) {
          lastMessageRef.current.scrollIntoView({ behavior: 'auto' });
        }
      };
      
      // Executar imediatamente
      scrollToLastMessage();
      
      // Executar após pequenos delays para garantir que mídias carreguem
      setTimeout(scrollToLastMessage, 50);
      setTimeout(scrollToLastMessage, 150);
      setTimeout(scrollToLastMessage, 300);
    }
  }, [loading, searchTerm, messagesList.length]);

  useEffect(() => {

    async function fetchData() {

      const settings = await getAll(companyId);

      let settinglgpdDeleteMessage;
      let settingEnableLGPD;

      for (const [key, value] of Object.entries(settings)) {

        if (key === "lgpdDeleteMessage") settinglgpdDeleteMessage = value
        if (key === "enableLGPD") settingEnableLGPD = value
      }
      if (settingEnableLGPD === "enabled" && settinglgpdDeleteMessage === "enabled") {
        setLGPDDeleteMessage(true);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setHasMore(true);
    currentTicketId.current = ticketId;
  }, [ticketId, selectedQueuesMessage]);

  useEffect(() => {
    if (ticketId === "undefined" || isNil(ticketId)) {
      history.push("/tickets");
      return;
    }

    dispatch({ type: "RESET" });
    setPageNumber(1);
    setHasMore(true);
    currentTicketId.current = ticketId;

    setLoading(true);
    const delayDebounceFn = setTimeout(async () => {
      try {
        const { data } = await api.get("/messages/" + ticketId, {
          params: { pageNumber: 1, selectedQueues: JSON.stringify(selectedQueuesMessage) },
        });

        dispatch({ type: "LOAD_MESSAGES_FIRST_PAGE", payload: data.messages });
        setHasMore(data.hasMore);
        setPageNumber(1);
      } catch (err) {
        console.error("Error loading messages:", err);
        toastError(err);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => {
      clearTimeout(delayDebounceFn);
      setLoading(false);
    };
  }, [ticketId, selectedQueuesMessage, history]);

  // Cleanup dos timers quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, [longPressTimer]);



  useEffect(() => {
    if (ticketId === "undefined") {
      return;
    }

    const companyId = user.companyId;

    //    const socket = socketManager.GetSocket();
    const connectEventMessagesList = () => {
      socket.emit("joinChatBox", `${ticketId}`);
    }

    const onAppMessageMessagesList = (data) => {
      if (data.action === "create" && data.ticket.uuid === ticketId) {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update" && data?.message?.ticket?.uuid === ticketId) {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }

      if (data.action == "delete" && data.message.ticket?.uuid === ticketId) {
        dispatch({ type: "DELETE_MESSAGE", payload: data.messageId });
      }
    }

    socket.on("connect", connectEventMessagesList);
    socket.on(`company-${companyId}-appMessage`, onAppMessageMessagesList);

    return () => {
      socket.emit("joinChatBoxLeave", `${ticketId}`)

      socket.off("connect", connectEventMessagesList);
      socket.off(`company-${companyId}-appMessage`, onAppMessageMessagesList);
    };

  }, [ticketId, companyId, user.companyId]);



  // useEffect específico para lidar com mudanças no searchTerm (desabilitado para navegação simples)
  useEffect(() => {
    // Desabilitar o processamento automático do searchTerm para navegação simples
    // O usuário quer apenas abrir a conversa, sem scroll automático
    if (searchTerm && searchTerm.trim() !== '') {
      // Log removido para evitar travamento
    }
  }, [searchTerm]);



  const loadMore = () => {
    if (loadingMore) return;
    setLoadingMore(true);
    
    const fetchMessages = async (page) => {
      try {
        const { data } = await api.get("/messages/" + ticketId, {
          params: { pageNumber: page, selectedQueues: JSON.stringify(selectedQueuesMessage) },
        });
        
        dispatch({ type: "LOAD_MESSAGES", payload: data.messages }); // Prepend for subsequent pages
        setHasMore(data.hasMore);
        setPageNumber(page);
      } catch (err) {
        console.error("Error loading more messages:", err);
        toastError(err);
        setHasMore(false);
      } finally {
        setLoadingMore(false);
      }
    };
    
    fetchMessages(pageNumber + 1);
  };

  const scrollToBottom = () => {
    const scrollToLastMessage = () => {
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({ behavior: 'auto' });
      }
    };
    
    // Executar imediatamente
    scrollToLastMessage();
    
    // Executar após pequenos delays para garantir que mídias carreguem
    setTimeout(scrollToLastMessage, 50);
    setTimeout(scrollToLastMessage, 150);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const hanldeReplyMessage = (e, message) => {
    //if (ticket.status === "open" || ticket.status === "group") {
    setAnchorEl(null);
    setReplyingMessage(message);
    //}
  };

  const handleAudioPlayed = async (message) => {
    try {
      // Enviar evento para o backend marcar o áudio como reproduzido
      const response = await api.post(`/messages/${message.id}/audio-played`);
      console.log('Áudio marcado como reproduzido:', message.id);
    } catch (error) {
      console.error('Erro ao marcar áudio como reproduzido:', error);
    }
  };


  const checkMessageMedia = (message) => {
  if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
    let locationParts = message.body.split('|')
    let imageLocation = locationParts[0]
    let linkLocation = locationParts[1]

    let descriptionLocation = null

    if (locationParts.length > 2)
      descriptionLocation = message.body.split('|')[2]

    return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
  } else

    if (message.mediaType === "contactMessage") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      // console.log(message)
      return <VcardPreview contact={contact} numbers={obj[0]?.number} queueId={message?.ticket?.queueId} whatsappId={message?.ticket?.whatsappId} />
    } else

      if (message.mediaType === "image") {
        return <ModalImageCors imageUrl={message.mediaUrl} />;
      } else

        if (message.mediaType === "audio") {
          return (
            <div className={classes.audioContainer}>
              <AudioModal 
                url={message.mediaUrl} 
                message={message}
                onAudioPlayed={() => handleAudioPlayed(message)}
              />
            </div>
            // <audio controls>
            //   <source src={message.mediaUrl} type="audio/ogg"></source>
            //   {/* <source src={message.mediaUrl} type="audio/mp3"></source> */}
            // </audio>
          );
        } else

          if (message.mediaType === "video") {
            return (
              <video
                className={classes.messageMedia}
                src={message.mediaUrl}
                controls
              />
            );
          } else {
            return (
              <>
                <div className={classes.downloadMedia}>
                  <Button
                    startIcon={<GetApp />}
                    variant="outlined"
                    target="_blank"
                    href={message.mediaUrl}
                  >
                    Download
                  </Button>
                </div>
                <Divider />
              </>
            );
          }
  };

  const renderMessageAck = (message) => {
  if (message.ack === 0) {
    // Mensagem enviada - check único (cinza)
    return <Done fontSize="small" className={classes.ackIcons} />;
  } else if (message.ack === 1) {
    // Mensagem entregue - dois checks cinzas
    return <DoneAll fontSize="small" className={classes.ackIcons} />;
  } else if (message.ack === 2 || message.ack === 3) {
    // Mensagem visualizada/lida - dois checks azuis
    return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
  } else if (message.ack === 4 || message.ack === 5) {
    // ack === 4 = áudio reproduzido, ack === 5 = confirmação final
    // Ambos significam que foi lido/reproduzido, então dois checks azuis
    return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
  }
  
  // Fallback - check único (cinza)
  return <Done fontSize="small" className={classes.ackIcons} />;
};

  const renderDailyTimestamps = (message, index) => {
  const today = format(new Date(), "dd/MM/yyyy")

  if (index === 0) {
    return (
      <span
        className={classes.dailyTimestamp}
        key={`timestamp-${message.id}`}
      >
        <div className={classes.dailyTimestampText}>
          {today === format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy") ? "HOJE" : format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
        </div>
      </span>
    );
  } else
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {today === format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy") ? "HOJE" : format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    } else
      if (index === messagesList.length - 1) {
        return (
          <div
            key={`ref-${message.id}`}
            ref={lastMessageRef}
            style={{ float: "left", clear: "both" }}
          />
        );
      }
};


  const renderTicketsSeparator = (message, index) => {
  let lastTicket = messagesList[index - 1]?.ticketId;
  let currentTicket = message.ticketId;

  if (lastTicket !== currentTicket && lastTicket !== undefined) {
    if (message?.ticket?.queue) {
      return (
        <span
          className={classes.currentTick}
          key={`timestamp-${message.id}a`}
        >
          <div
            className={classes.currentTicktText}
            style={{ backgroundColor: message?.ticket?.queue?.color || "grey" }}
          >
            #{i18n.t("ticketsList.called")} {message?.ticketId} - {message?.ticket?.queue?.name}
          </div>

        </span>
      );
    } else {
      return (
        <span
          className={classes.currentTick}
          key={`timestamp-${message.id}b`}
        >
          <div
            className={classes.currentTicktText}
            style={{ backgroundColor: "grey" }}
          >
            #{i18n.t("ticketsList.called")} {message.ticketId} - {i18n.t("ticketsList.noQueue")}
          </div>

        </span>
      );
    }
  }

};

  const renderMessageDivider = (message, index) => {
  if (index < messagesList.length && index > 0) {
    let messageUser = messagesList[index].fromMe;
    let previousMessageUser = messagesList[index - 1].fromMe;
    if (messageUser !== previousMessageUser) {
      return (

        <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
      );
    }
  }
};

  const path = require('path');

  const renderQuotedMessage = (message) => {

  return (
    <div
      className={clsx(classes.quotedContainerLeft, {
        [classes.quotedContainerRight]: message.fromMe,
      })}
    >
      <span
        className={clsx(classes.quotedSideColorLeft, {
          [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
        })}
      ></span>
      <div className={classes.quotedMsg}>
        {!message.quotedMsg?.fromMe && (
          <span className={classes.messageContactName}>
            {message.quotedMsg?.contact?.name}
          </span>
        )}

        {message.quotedMsg.mediaType === "audio"
          && (
            <div className={classes.downloadMedia}>
              <div className={classes.audioContainer}>
                <AudioModal url={message.quotedMsg.mediaUrl} />
              </div>

              {/* <audio controls>
                  <source src={message.quotedMsg.mediaUrl} type="audio/mp3"></source>
                  {/* <source src={message.quotedMsg.mediaUrl} type="audio/ogg"></source> 
                </audio> */}
            </div>
          )
        }
        {message.quotedMsg.mediaType === "video"
          && (
            <video
              className={classes.messageMedia}
              src={message.quotedMsg.mediaUrl}
              controls
            />
          )
        }
        {message.quotedMsg.mediaType === "contactMessage"
          && (
            "Contato"
          )
        }
        {message.quotedMsg.mediaType === "application"
          && (
            <div className={classes.downloadMedia}>
              <Button
                startIcon={<GetApp />}
                // color="primary"
                variant="outlined"
                target="_blank"
                href={message.quotedMsg.mediaUrl}
              >
                Download
              </Button>
            </div>
          )
        }

        {message.quotedMsg.mediaType === "image"
          && (
            <ModalImageCors imageUrl={message.quotedMsg.mediaUrl} />)
          || message.quotedMsg?.body}

        {!message.quotedMsg.mediaType === "image" && message.quotedMsg?.body}


      </div>
    </div>
  );
};

  const handleDrag = event => {
  event.preventDefault();
  event.stopPropagation();
  if (event.type === "dragenter" || event.type === "dragover") {
    setDragActive(true);
  } else if (event.type === "dragleave") {
    setDragActive(false);
  }
}

  const isYouTubeLink = (url) => {
  const youtubeRegex = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  return youtubeRegex.test(url);
};

const handleDrop = event => {
  event.preventDefault();
  event.stopPropagation();
  setDragActive(false);
  if (event.dataTransfer.files && event.dataTransfer.files[0]) {
    if (onDrop) {
      onDrop(event.dataTransfer.files);
    }
  }
}
const xmlRegex = /<([^>]+)>/g;
const boldRegex = /\*(.*?)\*/g;

const formatXml = (xmlString) => {
  // Verifica se o XML contém a assinatura com nome do atendente
  if (boldRegex.test(xmlString)) {
    // Formata o texto dentro da assinatura em negrito
    xmlString = xmlString.replace(boldRegex, "**$1**");
  }
  return xmlString;
};

const renderMessages = () => {
  // Verificação adicional para evitar problemas de renderização
  if (!messagesList || !Array.isArray(messagesList)) {
    console.warn("messagesList is not a valid array:", messagesList);
    return [];
  }

  if (messagesList.length > 0) {
    try {
      const viewMessagesList = messagesList.map((message, index) => {
        try {
          // Verificar se a mensagem contém o termo de busca
          const containsSearchTerm = searchTerm && 
            searchTerm.trim() !== '' && 
            message.body && 
            message.body.toLowerCase().includes(searchTerm.toLowerCase());

          if (message.mediaType === "call_log") {
            return (
              <React.Fragment key={message.id}>
                {renderDailyTimestamps(message, index)}
                {renderTicketsSeparator(message, index)}
                {renderMessageDivider(message, index)}
                <div className={classes.messageCenter}>
                  <IconButton
                    variant="contained"
                    size="small"
                    id="messageActionsButton"
                    disabled={message.isDeleted}
                    className={classes.messageActionsButton}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  >
                    <ExpandMore />
                  </IconButton>
                  {isGroup && (
                    <span className={classes.messageContactName}>
                      {message.contact?.name}
                    </span>
                  )}

                  {/* {isGroup && (
                      <span className={classes.messageContactName}>
                        {JSON.parse(message.dataJson).pushName} #{message.contact?.name}
                      </span>
                    )} */}
                  <div>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                      <path fill="#df3333" d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
                    </svg> <span>{i18n.t("ticketsList.missedCall")} {format(parseISO(message.createdAt), "HH:mm")}</span>
                  </div>
                </div>
              </React.Fragment>
            );
          }

          if (!message.fromMe) {
            return (
              <React.Fragment key={message.id}>
                {renderDailyTimestamps(message, index)}
                {renderTicketsSeparator(message, index)}
                {renderMessageDivider(message, index)}
                <div className={clsx(classes.messageContainer, classes.messageContainerLeft)}>
                  <div
                    className={clsx(classes.messageLeft, {
                      [classes.messageLeftButtons]: message.mediaType === "buttonsMessage",
                      [classes.messageLongPressing]: pressedMessageId === message.id
                    })}
                    title={message.queueId && message.queue?.name}
                    onDoubleClick={(e) => hanldeReplyMessage(e, message)}
                    onMouseDown={() => handleLongPressStart(message)}
                    onMouseUp={handleLongPressEnd}
                    onMouseLeave={handleLongPressCancel}
                    onTouchStart={() => handleLongPressStart(message)}
                    onTouchEnd={handleLongPressEnd}
                    onTouchCancel={handleLongPressCancel}
                  >
                    <div className={classes.messageContent}>
                      <IconButton
                        variant="contained"
                        size="small"
                        id="messageActionsButton"
                        disabled={message.isDeleted}
                        className={classes.messageActionsButton}
                        onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                      >
                        <ExpandMore />
                      </IconButton>

                      {message.isForwarded && (
                        <div>
                          <span className={classes.forwardMessage}
                          ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> Encaminhada
                          </span>
                          <br />
                        </div>
                      )}
                      {isGroup && (
                        <span className={classes.messageContactName}>
                          {message.contact?.name}
                        </span>
                      )}
                      {isYouTubeLink(message.body) && (
                        <>
                          <YouTubePreview videoUrl={message.body} />
                        </>
                      )}

                      {!lgpdDeleteMessage && message.isDeleted && (
                        <div>
                          <span className={classes.deletedMessage}
                          >🚫 Essa mensagem foi apagada pelo contato &nbsp;
                          </span>
                        </div>
                      )}

                      {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage"
                        //|| message.mediaType === "multi_vcard" 
                      ) && checkMessageMedia(message)}

                      <div className={clsx(classes.textContentItem, {
                        [classes.textContentItemDeleted]: message.isDeleted,
                      })}>
                        {message.quotedMsg && renderQuotedMessage(message)}
                        {
                          message.mediaType === "buttonsMessage" ? (
                            (() => {
                              let title = "";
                              let options = [];
                              try {
                                const data = JSON.parse(message.dataJson || "{}");
                                const bm = data?.message?.buttonsMessage;
                                if (bm && Array.isArray(bm.buttons)) {
                                  title = bm.contentText || bm.text || "";
                                  options = bm.buttons.map(b => ({ id: b.buttonId, text: b.buttonText?.displayText || "" }));
                                }
                              } catch (e) {}
                              if (options.length === 0) {
                                const bodyStr = String(message.body || "");
                                const cleaned = bodyStr.replace(/^\[BUTTONS\]\s*/i, "");
                                const idx = cleaned.indexOf("\n\n");
                                title = idx > -1 ? cleaned.slice(0, idx) : cleaned;
                                title = title.replace(/\*/g, "").trim();
                                const lines = cleaned.split("\n").map(l => l.trim()).filter(Boolean);
                                const opts = [];
                                for (const line of lines) {
                                  const m1 = line.match(/^\*\s*(.*?)\s*\*\s*-\s*(.+)$/);
                                  const m2 = line.match(/^\[(\d+)\]\s*-\s*(.+)$/);
                                  const m3 = line.match(/^\[(\d+)\]\s*(.+)$/);
                                  if (m1) { opts.push({ id: m1[1], text: m1[2] }); continue; }
                                  if (m2) { opts.push({ id: m2[1], text: m2[2] }); continue; }
                                  if (m3) { opts.push({ id: m3[1], text: m3[2] }); continue; }
                                }
                                options = opts;
                              }
                              return (
                                <div>
                                  {title && (
                                    <div className={classes.buttonsMessageHeader}>
                                      <MarkdownWrapper>{title}</MarkdownWrapper>
                                    </div>
                                  )}
                                  {options.length > 0 && (
                                    <div className={classes.buttonsMessageContainer}>
                                      {options.map((opt, idx) => (
                                        <Button key={`${message.id}-btn-${idx}`} variant="contained" size="small" className={clsx(classes.buttonsMessageButton, classes.buttonsMessageButtonDisabled)} disableElevation>
                                          <span className={classes.buttonsMessageButtonText} title={opt.text || String(opt.id)}>
                                            {truncateButtonText(opt.text || String(opt.id))}
                                          </span>
                                        </Button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()
                          ) : (
                            (
                              (message.mediaUrl !== null && (message.mediaType === "image" || message.mediaType === "video") && path.basename(message.mediaUrl).trim() !== message.body.trim()) ||
                              message.mediaType !== "audio" &&
                              message.mediaType !== "image" &&
                              message.mediaType !== "video" &&
                              message.mediaType != "reactionMessage" &&
                              message.mediaType != "locationMessage" && message.mediaType !== "contactMessage"
                            ) && (
                              <>
                                {xmlRegex.test(message.body) && (
                                  <span>{message.body}</span>
                                )}
                                {!xmlRegex.test(message.body) && (
                                  containsSearchTerm ? (
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: highlightSearchTerm(message.body, searchTerm)
                                      }}
                                    />
                                  ) : (
                                    <MarkdownWrapper>
                                      {(lgpdDeleteMessage && message.isDeleted) ? "🚫 _Mensagem apagada_ " : message.body}
                                    </MarkdownWrapper>
                                  )
                                )}
                              </>
                            )
                          )
                        }

                        {message.quotedMsg && message.mediaType === "reactionMessage" && (
                          <>
                            <span style={{ marginLeft: "0px" }}>
                              <MarkdownWrapper>
                                {"" + message?.contact?.name + " reagiu... " + message.body}
                              </MarkdownWrapper>
                            </span>
                          </>
                        )}

                        <span className={classes.timestamp}>
                          {message.isEdited ? "Editada " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                          {message.fromMe && renderMessageAck(message)}
                        </span>
                      </div>
                    </div>
                  </div>
                  {isSelectionMode && (
                    <SelectMessageCheckbox
                      message={message}
                      classes={classes}
                    />
                  )}
                </div>
              </React.Fragment>
            );
          } else {
            return (
              <React.Fragment key={message.id}>
                {renderDailyTimestamps(message, index)}
                {renderTicketsSeparator(message, index)}
                {renderMessageDivider(message, index)}
                <div className={clsx(classes.messageContainer, classes.messageContainerRight)}>
                  {isSelectionMode && (
                    <SelectMessageCheckbox
                      message={message}
                      classes={classes}
                    />
                  )}
                  <div
                    className={clsx(message.isPrivate ? classes.messageRightPrivate : classes.messageRight, {
                      [classes.messageRightButtons]: message.mediaType === "buttonsMessage",
                      [classes.messageLongPressing]: pressedMessageId === message.id
                    })}
                    title={message.queueId && message.queue?.name}
                    onDoubleClick={(e) => hanldeReplyMessage(e, message)}
                    onMouseDown={() => handleLongPressStart(message)}
                    onMouseUp={handleLongPressEnd}
                    onMouseLeave={handleLongPressCancel}
                    onTouchStart={() => handleLongPressStart(message)}
                    onTouchEnd={handleLongPressEnd}
                    onTouchCancel={handleLongPressCancel}
                  >
                    <div className={classes.messageContent}>
                      <IconButton
                        variant="contained"
                        size="small"
                        id="messageActionsButton"
                        disabled={message.isDeleted}
                        className={classes.messageActionsButton}
                        onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                      >
                        <ExpandMore />
                      </IconButton>
                      {message.isForwarded && (
                        <div>
                          <span className={classes.forwardMessage}
                          ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> Encaminhada
                          </span>
                          <br />
                        </div>
                      )}
                      {isYouTubeLink(message.body) && (
                        <>
                          <YouTubePreview videoUrl={message.body} />
                        </>
                      )}
                      {!lgpdDeleteMessage && message.isDeleted && (
                        <div>
                          <span className={classes.deletedMessage}
                          >🚫 Essa mensagem foi apagada &nbsp;
                          </span>
                        </div>
                      )}
                      {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage"
                        //|| message.mediaType === "multi_vcard" 
                      ) && checkMessageMedia(message)}

                      <div className={clsx(classes.textContentItem, {
                        [classes.textContentItemDeleted]: message.isDeleted,
                      })}>
                        {message.quotedMsg && renderQuotedMessage(message)}
                        {
                          message.mediaType === "buttonsMessage" ? (
                            (() => {
                              let title = "";
                              let options = [];
                              try {
                                const data = JSON.parse(message.dataJson || "{}");
                                const bm = data?.message?.buttonsMessage;
                                if (bm && Array.isArray(bm.buttons)) {
                                  title = bm.contentText || bm.text || "";
                                  options = bm.buttons.map(b => ({ id: b.buttonId, text: b.buttonText?.displayText || "" }));
                                }
                              } catch (e) {}
                              if (options.length === 0) {
                                const bodyStr = String(message.body || "");
                                const cleaned = bodyStr.replace(/^\[BUTTONS\]\s*/i, "");
                                const idx = cleaned.indexOf("\n\n");
                                title = idx > -1 ? cleaned.slice(0, idx) : cleaned;
                                title = title.replace(/\*/g, "").trim();
                                const lines = cleaned.split("\n").map(l => l.trim()).filter(Boolean);
                                const opts = [];
                                for (const line of lines) {
                                  const m1 = line.match(/^\*\s*(.*?)\s*\*\s*-\s*(.+)$/);
                                  const m2 = line.match(/^\[(\d+)\]\s*-\s*(.+)$/);
                                  const m3 = line.match(/^\[(\d+)\]\s*(.+)$/);
                                  if (m1) { opts.push({ id: m1[1], text: m1[2] }); continue; }
                                  if (m2) { opts.push({ id: m2[1], text: m2[2] }); continue; }
                                  if (m3) { opts.push({ id: m3[1], text: m3[2] }); continue; }
                                }
                                options = opts;
                              }
                              return (
                                <div>
                                  {title && (
                                    <div className={classes.buttonsMessageHeader}>
                                      <MarkdownWrapper>{title}</MarkdownWrapper>
                                    </div>
                                  )}
                                  {options.length > 0 && (
                                    <div className={classes.buttonsMessageContainer}>
                                      {options.map((opt, idx) => (
                                        <Button key={`${message.id}-btn-${idx}`} variant="contained" size="small" className={clsx(classes.buttonsMessageButton, classes.buttonsMessageButtonDisabled)} disableElevation>
                                          <span className={classes.buttonsMessageButtonText} title={opt.text || String(opt.id)}>
                                            {truncateButtonText(opt.text || String(opt.id))}
                                          </span>
                                        </Button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()
                          ) : (
                            (
                              (message.mediaUrl !== null && (message.mediaType === "image" || message.mediaType === "video") && path.basename(message.mediaUrl).trim() !== message.body.trim()) ||
                              message.mediaType !== "audio" &&
                              message.mediaType !== "image" &&
                              message.mediaType !== "video" &&
                              message.mediaType != "reactionMessage" &&
                              message.mediaType != "locationMessage" && message.mediaType !== "contactMessage"
                            ) && (
                              <>
                                {xmlRegex.test(message.body) && (
                                  <span>{message.body}</span>
                                )}
                                {!xmlRegex.test(message.body) && (
                                  containsSearchTerm ? (
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: highlightSearchTerm(message.body, searchTerm)
                                      }}
                                    />
                                  ) : (
                                    <MarkdownWrapper>
                                      {(lgpdDeleteMessage && message.isDeleted) ? "🚫 _Mensagem apagada_ " : message.body}
                                    </MarkdownWrapper>
                                  )
                                )}
                              </>
                            )
                          )
                        }

                        {message.quotedMsg && message.mediaType === "reactionMessage" && (
                          <>
                            <span style={{ marginLeft: "0px" }}>
                              <MarkdownWrapper>
                                {"" + message?.contact?.name + " reagiu... " + message.body}
                              </MarkdownWrapper>
                            </span>
                          </>
                        )}

                        <span className={classes.timestamp}>
                          {message.isEdited ? "Editada " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                          {message.fromMe && renderMessageAck(message)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          }
        } catch (error) {
          console.error("Error rendering message:", message.id, error);
          return null; // Retornar null em caso de erro para não quebrar a renderização
        }
      });

      return viewMessagesList.filter(item => item !== null); // Filtrar itens nulos
    } catch (error) {
      console.error("Error rendering messages:", error);
      return [];
    }
  }
  return []; // Sempre retornar um array vazio em vez de null
};

return (
  <div className={classes.messagesListWrapper} onDragEnter={handleDrag}>
    {dragActive && <div className={classes.dragElement} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>Solte o arquivo aqui</div>}

    <MessageOptionsMenu
      message={selectedMessage}
      anchorEl={anchorEl}
      menuOpen={messageOptionsMenuOpen}
      handleClose={handleCloseMessageOptionsMenu}
      isGroup={isGroup}
      whatsappId={whatsappId}
      queueId={queueId}
    />
    

    
    <div
      id="messagesList"
      className={classes.messagesList}
      onScroll={handleScroll}
    >
      {messagesList && messagesList.length > 0 ?
        renderMessages()
        : []}
    </div>

    {(channel !== "whatsapp" && channel !== undefined) && (
      <div
        style={{
          width: "100%",
          display: "flex",
          padding: "10px",
          alignItems: "center",
          backgroundColor: "#E1F3FB",
        }}
      >
        {channel === "facebook" ? (
          <Facebook />
        ) : (
          <Instagram />
        )}

        <span>
          Você tem 24h para responder após receber uma mensagem, de acordo
          com as políticas do Facebook.
        </span>
      </div>
    )}
    {(loading || loadingMore) && (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
        position: 'absolute',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000
      }}>
        <CircularProgress size={24} />
      </div>
    )}
  </div>
);
};

export default MessagesList;
